import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Plus, X } from '../../../../../components/icon/solar-icons.js'
import {
    addTeamMember,
    createSettingsTeam,
    deleteSettingsTeam,
    fetchSettingsTeams,
    fetchTeamMembers,
    removeTeamMember,
    type SettingsTeam,
    type SettingsTeamMember,
    updateSettingsTeam,
} from '../../../utils/portal-api.js'
import { PolicyEmployeePicker } from './policy-employee-picker.js'

interface Company {
    id: number
    name: string
    reg_code: string | null
}

export interface TeamsSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
    companies: Company[]
}

interface TeamForm {
    name: string
    company_id: number
}

export const TeamsSection = ({ lang, showToast, companies }: TeamsSectionProps) => {
    const s = t.portal.settings.teams
    const [teams, setTeams] = useState<SettingsTeam[]>([])
    const [companyFilter, setCompanyFilter] = useState<number | 'all'>('all')
    const [slideOpen, setSlideOpen] = useState(false)
    const [editing, setEditing] = useState<SettingsTeam | null>(null)
    const [form, setForm] = useState<TeamForm>({
        name: '',
        company_id: companies[0]?.id ?? 0,
    })
    const [saving, setSaving] = useState(false)
    const [members, setMembers] = useState<SettingsTeamMember[]>([])

    const reload = async () => {
        const filterArg = companyFilter === 'all' ? undefined : companyFilter
        const r = await fetchSettingsTeams(filterArg)
        if (r.ok) setTeams(r.data)
    }

    const reloadMembers = async (teamId: number) => {
        const r = await fetchTeamMembers(teamId)
        if (r.ok) setMembers(r.data)
    }

    useEffect(() => {
        void reload()
    }, [companyFilter])

    const openCreate = () => {
        setEditing(null)
        setForm({ name: '', company_id: companies[0]?.id ?? 0 })
        setMembers([])
        setSlideOpen(true)
    }

    const openEdit = (team: SettingsTeam) => {
        setEditing(team)
        setForm({ name: team.name, company_id: team.company_id ?? companies[0]?.id ?? 0 })
        setSlideOpen(true)
        void reloadMembers(team.id)
    }

    const close = () => {
        setSlideOpen(false)
        setEditing(null)
        setMembers([])
    }

    const save = async () => {
        if (!form.name || !form.company_id) return
        setSaving(true)
        try {
            if (editing) {
                const update: { name?: string; company_id?: number } = {}
                if (form.name !== editing.name) update.name = form.name
                if (form.company_id !== editing.company_id) update.company_id = form.company_id
                if (Object.keys(update).length === 0) {
                    close()
                    return
                }
                const r = await updateSettingsTeam(editing.id, update)
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                showToast(s.updated(lang), 'success')
            } else {
                const r = await createSettingsTeam({
                    name: form.name,
                    company_id: form.company_id,
                })
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                showToast(s.created(lang), 'success')
            }
            close()
            await reload()
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (team: SettingsTeam) => {
        const r = await deleteSettingsTeam(team.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        showToast(s.deleted(lang), 'success')
        await reload()
    }

    const handleAddMember = async (employee: SettingsTeamMember) => {
        if (!editing) return
        const r = await addTeamMember(editing.id, employee.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        await reloadMembers(editing.id)
        await reload()
    }

    const handleRemoveMember = async (employeeId: number) => {
        if (!editing) return
        const r = await removeTeamMember(editing.id, employeeId)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        await reloadMembers(editing.id)
        await reload()
    }

    const handleAddMany = async (employees: SettingsTeamMember[]) => {
        if (!editing) return
        for (const emp of employees) {
            if (members.some((m) => m.id === emp.id)) continue
            await addTeamMember(editing.id, emp.id)
        }
        await reloadMembers(editing.id)
        await reload()
    }

    const handleRemoveAll = async () => {
        if (!editing) return
        for (const m of members) {
            await removeTeamMember(editing.id, m.id)
        }
        await reloadMembers(editing.id)
        await reload()
    }

    const memberIds = members.map((m) => m.id)
    const companyName = (cid: number | null) => companies.find((c) => c.id === cid)?.name ?? '—'
    const companyChangeBlocked = !!(editing && editing.member_count > 0)

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <div className="portal-settings__header-controls">
                    {companies.length > 1 ? (
                        <Select
                            className="portal-settings__input--compact"
                            value={String(companyFilter)}
                            onChange={(value) =>
                                setCompanyFilter(value === 'all' ? 'all' : Number(value))
                            }
                            options={[
                                { value: 'all', label: s.filterAllCompanies(lang) },
                                ...companies.map((c) => ({
                                    value: String(c.id),
                                    label: c.name,
                                })),
                            ]}
                        />
                    ) : null}
                    <Button
                        variant="primary"
                        size="small"
                        iconLeft={Plus}
                        text={s.createTeam(lang)}
                        onClick={openCreate}
                    />
                </div>
            </div>

            {teams.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noTeams(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {teams.map((team) => (
                        <div key={team.id} className="portal-settings__data-row">
                            <div className="portal-settings__data-info">
                                <div className="portal-settings__data-name">{team.name}</div>
                                <div className="portal-settings__data-meta">
                                    {companyName(team.company_id)} · {s.memberCount(lang)}:{' '}
                                    {team.member_count}
                                </div>
                            </div>
                            <div className="portal-settings__data-actions">
                                <button type="button" onClick={() => openEdit(team)}>
                                    {s.edit(lang)}
                                </button>
                                <button
                                    type="button"
                                    className="is-danger"
                                    onClick={() => void handleDelete(team)}
                                    disabled={team.member_count > 0}
                                    title={team.member_count > 0 ? s.cannotDelete(lang) : undefined}
                                >
                                    {s.delete(lang)}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={close} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {editing ? s.editTeam(lang) : s.createTeam(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.name(lang)}
                                    value={form.name}
                                    onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                                />
                            </div>

                            <div className="portal-slide-over__field">
                                <Select
                                    label={s.company(lang)}
                                    value={String(form.company_id)}
                                    isDisabled={companyChangeBlocked}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            company_id: Number(value),
                                        }))
                                    }
                                    options={companies.map((c) => ({
                                        value: String(c.id),
                                        label: c.name,
                                    }))}
                                />
                                {companyChangeBlocked ? (
                                    <small
                                        style={{
                                            color: 'var(--portal-text-secondary)',
                                        }}
                                    >
                                        {s.companyChangeBlocked(lang)}
                                    </small>
                                ) : null}
                            </div>

                            {editing ? (
                                <div className="portal-slide-over__field">
                                    <label>{s.members(lang)}</label>
                                    <PolicyEmployeePicker
                                        lang={lang}
                                        selectedIds={memberIds}
                                        selectedEmployees={members}
                                        onAdd={(emp) => void handleAddMember(emp)}
                                        onRemove={(id) => void handleRemoveMember(id)}
                                        onSelectAll={(emps) => void handleAddMany(emps)}
                                        onClearAll={() => void handleRemoveAll()}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={saving || !form.name || !form.company_id}
                                onClick={() => void save()}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
